import { isValidElement, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Link } from "@inertiajs/react";

const INTERNAL_HOSTS = new Set(["learntokiz.io", "www.learntokiz.io"]);

function isInternalHref(href: string): boolean {
  if (!href) return true;
  if (href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) return true;
  if (href.startsWith("/") && !href.startsWith("//")) return true;
  try {
    const url = new URL(href, typeof window !== "undefined" ? window.location.origin : "https://learntokiz.io");
    if (typeof window !== "undefined" && url.origin === window.location.origin) return true;
    return INTERNAL_HOSTS.has(url.hostname);
  } catch {
    return true;
  }
}

// Content between <callout>…</callout> arrives as raw text nodes from
// rehype-raw. This helper extracts that text and re-runs it through
// ReactMarkdown so **bold**, [links], `code`, etc. render as intended
// inside custom MDX components.
function nodeToText(node: ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(nodeToText).join("");
  if (isValidElement(node)) {
    const props = node.props as { children?: ReactNode };
    return nodeToText(props.children);
  }
  return "";
}

interface Props {
  children: ReactNode;
}

export function InlineMarkdown({ children }: Props) {
  const source = nodeToText(children).trim();
  if (!source) return null;

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        // Flatten paragraphs so single-line bodies don't get a stray top/bottom
        // margin against the surrounding chrome (callout icon, quote mark).
        p: ({ children: c }) => <>{c}</>,
        a: ({ href, children: c, ...rest }) => {
          const url = href ?? "";
          if (!isInternalHref(url)) {
            return (
              <a href={url} target="_blank" rel="noopener noreferrer" {...rest}>
                {c}
              </a>
            );
          }
          if (url.startsWith("#") || url.startsWith("mailto:") || url.startsWith("tel:")) {
            return <a href={url} {...rest}>{c}</a>;
          }
          return <Link href={url} {...rest}>{c}</Link>;
        },
      }}
    >
      {source}
    </ReactMarkdown>
  );
}

